import { Button } from "@faire/slate/components/button";
import { ChevronDown } from "@faire/slate-icons/ChevronDown";
import { ChevronUp } from "@faire/slate-icons/ChevronUp";
import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { SHOPIFY_MIXED_RTL_PRODUCTS } from "../leBonShoppeProducts";

export type ShopifyImportToastPhase = "importing" | "success" | "error";

export type ShopifyImportToastProps = {
  visible: boolean;
  phase: ShopifyImportToastPhase;
  completed: number;
  total: number;
  autoPublish: boolean;
  importProductIds: string[];
  onDismiss: () => void;
};

const AUTO_DISMISS_MS = 5000;

/** Prototype replica of brand-portal `WebBridgeImportToast` (Catalog Connect import snackbar). */
export const ShopifyImportToast: React.FC<ShopifyImportToastProps> = ({
  visible,
  phase,
  completed,
  total,
  autoPublish,
  importProductIds,
  onDismiss,
}) => {
  const history = useHistory();
  const location = useLocation();
  const [shouldShowContent, setShouldShowContent] = useState(true);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isOnBulkEditorPage = location.pathname.includes("/bulk-editor");
  const isComplete = phase === "success";
  const isError = phase === "error";
  const isStarting = phase === "importing" && completed === 0;

  useEffect(() => {
    if (!visible) {
      setShouldShowContent(true);
    }
  }, [visible]);

  useEffect(() => {
    if (!isComplete) return;
    dismissTimerRef.current = setTimeout(() => {
      onDismiss();
    }, AUTO_DISMISS_MS);
    return () => {
      if (dismissTimerRef.current) {
        clearTimeout(dismissTimerRef.current);
        dismissTimerRef.current = null;
      }
    };
  }, [isComplete, onDismiss]);

  if (!visible) return null;

  const headerText = (() => {
    if (isError) return "Import failed.";
    if (isStarting) return "Starting Catalog Connect import...";
    if (isComplete) {
      return `${completed} of ${total} product${total === 1 ? "" : "s"} imported.`;
    }
    return `${completed} of ${total} product${total === 1 ? "" : "s"} imported...`;
  })();

  const contentMessage = (() => {
    if (isError) return "Import failed.";
    if (isComplete) {
      if (autoPublish) return "Import complete!";
      return "Saved as drafts.";
    }
    if (isStarting) return "Loading...";
    return "Processing...";
  })();

  const handleViewProducts = () => {
    if (total === 1 && importProductIds.length === 1) {
      const product = SHOPIFY_MIXED_RTL_PRODUCTS.find(
        (p) => p.id === importProductIds[0]
      );
      if (product) {
        history.push("/hd/shopify-bestseller-nudge/spe", { product });
      }
    } else {
      history.push("/hd/shopify-bestseller-nudge/bulk-editor", {
        productIds: importProductIds,
      });
    }
    onDismiss();
  };

  const ChevronIcon = shouldShowContent ? ChevronDown : ChevronUp;

  return (
    <div
      className="fixed right-8 bottom-0 z-[10000] w-[328px] shadow-lg"
      data-test-id="webbridge-import-toast-prototype"
    >
      <div
        className="flex items-center justify-between rounded-t-sm p-4"
        style={{
          backgroundColor: "var(--slate-color-surface-primary-inverse)",
        }}
      >
        <p
          className="type-fs-paragraph-medium"
          style={{
            color: "var(--slate-color-text-primary-inverse)",
            margin: 0,
            flex: 1,
            paddingRight: 8,
          }}
        >
          {headerText}
        </p>
        <Button
          variant="plain"
          onClick={() => setShouldShowContent((v) => !v)}
          data-test-id="webbridge-import-toast-toggle"
          aria-label={
            shouldShowContent
              ? "Collapse import details"
              : "Expand import details"
          }
        >
          <ChevronIcon
            width="16px"
            height="16px"
            style={{ color: "var(--slate-color-action-text-inverse)" }}
          />
        </Button>
      </div>
      {shouldShowContent ? (
        <div
          className="flex items-center justify-between gap-2 border-t p-4"
          style={{
            borderColor: "var(--slate-color-border-subdued)",
            backgroundColor: "var(--slate-color-surface-primary)",
          }}
        >
          <p
            className="type-fs-paragraph"
            style={{
              color: "var(--slate-color-text-primary)",
              margin: 0,
              flex: 1,
            }}
          >
            {contentMessage}
          </p>
          {!isOnBulkEditorPage ? (
            <Button
              variant="plain"
              onClick={handleViewProducts}
              data-test-id="webbridge-import-toast-view-products"
            >
              View products
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
